import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT } from '../../constants';
import { fetchWrapper } from '../../helpers';
import {
  ERROR_STATES,
  PERSIST_DATA_STATES,
  STATE_IDS,
} from '../../components/layout/treatment-plan/initialAssessment/constants/states';

import type {
  IAssessmentSummary,
  IAssessmentTool,
  IAssessmentVisual,
  IInitialAssessment,
  IOption,
} from '../../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'initialAssessment';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<InitialAssessmentState>) =>
    createExtraReducers(builder),
});

export interface InitialAssessmentState {
  treatmentPlanId: number | null;
  assessment: IInitialAssessment | null;
  summary: IAssessmentSummary;
  error: any;
  tools: Array<IAssessmentTool>;
  loading: boolean;
  toolsLoading: boolean;
  barriers: Array<IOption>;
  barriersLoading: boolean;
  errorState: any;
  persistDataState: any;
}

type InitialAssessmentStateUpdate = {
  [Key in keyof InitialAssessmentState]: { key: Key; data: InitialAssessmentState[Key] };
}[keyof InitialAssessmentState];

function createInitialState(): InitialAssessmentState {
  return {
    treatmentPlanId: null,
    assessment: null,
    loading: false,
    summary: {},
    tools: [],
    error: null,
    toolsLoading: false,
    barriers: [],
    barriersLoading: false,
    errorState: {},
    persistDataState: {},
  };
}

function createReducers() {
  return {
    clearState,
    updateState,
    updateErrorState,
    updatePersistDataState,
  };

  function updateState(
    state: InitialAssessmentState,
    action: PayloadAction<InitialAssessmentStateUpdate>,
  ) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }

  function clearState(state: InitialAssessmentState) {
    state.assessment = null;
    state.error = null;
    state.summary = {};
    state.tools = [];
    state.loading = false;
    state.toolsLoading = false;
    state.barriers = [];
    state.barriersLoading = false;
  }

  function updateErrorState(state: InitialAssessmentState, action: any) {
    state.errorState = state.errorState
      ? {
          ...state.errorState,
          ...action.payload.data,
        }
      : action.payload.data;
  }

  function updatePersistDataState(state: InitialAssessmentState, action: any) {
    state.persistDataState = state.persistDataState
      ? {
          ...state.persistDataState,
          ...action.payload.data,
        }
      : action.payload.data;
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/treatment_plans`;

  return {
    getAssessment: getAssessment(),
    createInitialAssessment: createInitialAssessment(),
    updateInitialAssessment: updateInitialAssessment(),
    deleteInitialAssessment: deleteInitialAssessment(),
    getTools: getTools(),
    createTool: createTool(),
    updateTool: updateTool(),
    deleteTool: deleteTool(),
    getToolDocuments: getToolDocuments(),
    createToolDocument: createToolDocument(),
    updateToolDocument: updateToolDocument(),
    updateToolDocumentImage: updateToolDocumentImage(),
    deleteToolDocument: deleteToolDocument(),
    createSummary: createSummary(),
    updateSummary: updateSummary(),
    getBarriers: getBarriers(),
    createBarrier: createBarrier(),
    updateBarrier: updateBarrier(),
    deleteBarrier: deleteBarrier(),
  };

  function getAssessment() {
    return createAsyncThunk(
      `${name}/getAssessment`,
      async ({ getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/assessments/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createInitialAssessment() {
    return createAsyncThunk(
      `${name}/createInitialAssessment`,
      async ({ data, getAccessTokenSilently, planId }: any) => {
        const response = await fetchWrapper.post(
          { url: `${baseUrl}/${planId}/assessments/`, getAccessTokenSilently },
          data,
        );
        return response;
      },
    );
  }

  function updateInitialAssessment() {
    return createAsyncThunk(
      `${name}/updateInitialAssessment`,
      async ({ data, getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteInitialAssessment() {
    return createAsyncThunk(
      `${name}/deleteInitialAssessment`,
      async ({ getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/assessments/${assessmentId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getTools() {
    return createAsyncThunk(
      `${name}/getTools`,
      async ({ getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createTool() {
    return createAsyncThunk(
      `${name}/createTool`,
      async ({ data, getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateTool() {
    return createAsyncThunk(
      `${name}/updateTool`,
      async ({ data, getAccessTokenSilently, planId, assessmentId, toolId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/${toolId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteTool() {
    return createAsyncThunk(
      `${name}/deleteTool`,
      async ({ getAccessTokenSilently, planId, assessmentId, toolId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/${toolId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function getToolDocuments() {
    return createAsyncThunk(
      `${name}/getToolDocuments`,
      async ({ getAccessTokenSilently, planId, assessmentId, toolId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/${toolId}/documents/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createToolDocument() {
    return createAsyncThunk(
      `${name}/createToolDocument`,
      async ({ data, getAccessTokenSilently, planId, assessmentId, toolId }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/${toolId}/documents/`,
            getAccessTokenSilently,
          },
          data,
          'multipart/form-data',
        );
        return response;
      },
    );
  }
  function updateToolDocument() {
    return createAsyncThunk(
      `${name}/updateToolDocument`,
      async ({ data, getAccessTokenSilently, planId, assessmentId, toolId, documentId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/${toolId}/documents/${documentId}/`,
            getAccessTokenSilently,
          },
          data,
          'application/x-www-form-urlencoded;charset=UTF-8',
        );
        return response;
      },
    );
  }

  function updateToolDocumentImage() {
    return createAsyncThunk(
      `${name}/updateToolDocumentImage`,
      async ({ data, getAccessTokenSilently, planId, assessmentId, toolId, documentId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/${toolId}/documents/${documentId}/reupload_visual/`,
            getAccessTokenSilently,
          },
          data,
          'application/x-www-form-urlencoded;charset=UTF-8',
        );
        return response;
      },
    );
  }

  function deleteToolDocument() {
    return createAsyncThunk(
      `${name}/deleteToolDocument`,
      async ({ getAccessTokenSilently, planId, assessmentId, toolId, documentId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/assessments/${assessmentId}/tools/${toolId}/documents/${documentId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }

  function createSummary() {
    return createAsyncThunk(
      `${name}/createSummary`,
      async ({ data, getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/summarize/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateSummary() {
    return createAsyncThunk(
      `${name}/updateSummary`,
      async ({ data, getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/summarize/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function getBarriers() {
    return createAsyncThunk(
      `${name}/getBarriers`,
      async ({ getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.get({
          url: `${baseUrl}/${planId}/assessments/${assessmentId}/barriers/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
  function createBarrier() {
    return createAsyncThunk(
      `${name}/createBarrier`,
      async ({ data, getAccessTokenSilently, planId, assessmentId }: any) => {
        const response = await fetchWrapper.post(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/barriers/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function updateBarrier() {
    return createAsyncThunk(
      `${name}/updateBarrier`,
      async ({ data, getAccessTokenSilently, planId, assessmentId, barrierId }: any) => {
        const response = await fetchWrapper.patch(
          {
            url: `${baseUrl}/${planId}/assessments/${assessmentId}/barriers/${barrierId}/`,
            getAccessTokenSilently,
          },
          data,
        );
        return response;
      },
    );
  }

  function deleteBarrier() {
    return createAsyncThunk(
      `${name}/deleteBarrier`,
      async ({ getAccessTokenSilently, planId, assessmentId, barrierId }: any) => {
        const response = await fetchWrapper.delete({
          url: `${baseUrl}/${planId}/assessments/${assessmentId}/barriers/${barrierId}/`,
          getAccessTokenSilently,
        });
        return response;
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<InitialAssessmentState>) {
  const {
    getAssessment,
    createInitialAssessment,
    updateInitialAssessment,
    getTools,
    createTool,
    updateTool,
    deleteTool,
    createSummary,
    updateSummary,
    getBarriers,
    createBarrier,
    deleteBarrier,
    updateBarrier,
    getToolDocuments,
    deleteToolDocument,
    createToolDocument,
    updateToolDocument,
    updateToolDocumentImage,
  } = extraActions;

  builder
    // getAssessment()
    .addCase(getAssessment.pending, (state: InitialAssessmentState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(
      getAssessment.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<IInitialAssessment>) => {
        const {
          summary_observations_during_assessment,
          summary_assessment_result,
          summary_priorities_based_on_assessment,
        } = action.payload;
        state.assessment = action.payload;
        state.summary = {
          summary_observations_during_assessment,
          summary_assessment_result,
          summary_priorities_based_on_assessment,
        };
        state.loading = false;
      },
    )
    .addCase(getAssessment.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // createInitialAssessment()
    .addCase(createInitialAssessment.pending, (state: InitialAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createInitialAssessment.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<IInitialAssessment>) => {
        state.loading = false;
        state.assessment = action.payload;
      },
    )
    .addCase(createInitialAssessment.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateInitialAssessment()
    .addCase(updateInitialAssessment.pending, (state: InitialAssessmentState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateInitialAssessment.fulfilled, (state: InitialAssessmentState) => {
      state.loading = false;
    })
    .addCase(updateInitialAssessment.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // getTools()
    .addCase(getTools.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
      state.error = null;
    })
    .addCase(
      getTools.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<Array<IAssessmentTool>>) => {
        state.toolsLoading = false;
        state.tools = action.payload;
      },
    )
    .addCase(getTools.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // createTool()
    .addCase(createTool.pending, (state: InitialAssessmentState) => {
      state.error = null;
      state.toolsLoading = true;
    })
    .addCase(
      createTool.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<IAssessmentTool>) => {
        const currentTPPersistedData = state.persistDataState ? state.persistDataState : {};
        delete currentTPPersistedData[`${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-new`];
        state.tools = [...state.tools, action.payload];
        state.toolsLoading = false;
      },
    )
    .addCase(createTool.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // updateTool()
    .addCase(updateTool.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
    })
    .addCase(updateTool.fulfilled, (state: InitialAssessmentState) => {
      state.toolsLoading = false;
    })
    .addCase(updateTool.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // deleteTool()
    .addCase(deleteTool.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
      state.error = null;
    })
    .addCase(
      deleteTool.fulfilled,
      (
        state: InitialAssessmentState,
        action: PayloadAction<IAssessmentTool, string, { arg: { toolId: number } }>,
      ) => {
        const currentTPErrors = state.errorState || {};
        const currentTPPersistedData = state.persistDataState || {};

        const newErrorState = { ...currentTPErrors };
        delete newErrorState[
          `${ERROR_STATES[STATE_IDS.ASSESSMENT_METHOD]}-${action.meta.arg.toolId}`
        ];
        delete newErrorState[`${ERROR_STATES[STATE_IDS.NO_VISUAL]}-${action.meta.arg.toolId}`];
        for (const key in newErrorState) {
          if (key.includes(`${ERROR_STATES[STATE_IDS.VISUAL]}-${action.meta.arg.toolId}`)) {
            delete newErrorState[key];
          }
        }
        state.errorState = newErrorState;

        delete currentTPPersistedData[
          `${PERSIST_DATA_STATES[STATE_IDS.ASSESSMENT_METHOD]}-${action.meta.arg.toolId}`
        ];
        for (const key in currentTPPersistedData) {
          if (key.includes(`${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${action.meta.arg.toolId}`)) {
            delete currentTPPersistedData[key];
          }
        }
        state.persistDataState = currentTPPersistedData;
        state.toolsLoading = false;
        state.tools = state.tools.filter((tool) => tool.id !== action.meta.arg.toolId);
      },
    )
    .addCase(deleteTool.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // createSummary()
    .addCase(createSummary.pending, (state: InitialAssessmentState) => {
      state.error = null;
      state.loading = false;
    })
    .addCase(
      createSummary.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<IAssessmentSummary>) => {
        state.summary = action.payload;
        state.loading = false;
      },
    )
    .addCase(createSummary.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // updateSummary()
    .addCase(updateSummary.pending, (state: InitialAssessmentState) => {
      state.error = null;
      state.loading = true;
    })
    .addCase(updateSummary.fulfilled, (state: InitialAssessmentState, action: any) => {
      const {
        summary_observations_during_assessment,
        summary_assessment_result,
        summary_priorities_based_on_assessment,
      } = action.payload;
      state.summary = {
        ...state.summary,
        summary_observations_during_assessment,
        summary_assessment_result,
        summary_priorities_based_on_assessment,
      };
      state.loading = false;
    })
    .addCase(updateSummary.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(state.error.message);
    })
    // getBarriers()
    .addCase(getBarriers.pending, (state: InitialAssessmentState) => {
      state.error = null;
      state.barriersLoading = true;
    })
    .addCase(
      getBarriers.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<Array<IOption>>) => {
        state.barriers = action.payload;
        state.barriersLoading = false;
      },
    )
    .addCase(getBarriers.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.barriersLoading = false;
      toast.error(state.error.message);
    })
    // createBarrier()
    .addCase(createBarrier.pending, (state: InitialAssessmentState) => {
      state.error = null;
      state.barriersLoading = true;
    })
    .addCase(
      createBarrier.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<IOption>) => {
        state.barriers = [action.payload, ...state.barriers];
        state.barriersLoading = false;
      },
    )
    .addCase(createBarrier.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.barriersLoading = false;
      toast.error(state.error.message);
    })
    // deleteBarrier()
    .addCase(deleteBarrier.pending, (state: InitialAssessmentState) => {
      state.barriersLoading = true;
      state.error = null;
    })
    .addCase(
      deleteBarrier.fulfilled,
      (
        state: InitialAssessmentState,
        action: PayloadAction<IOption, string, { arg: { barrierId: number } }>,
      ) => {
        const newErrorState = { ...state.errorState };
        delete newErrorState[
          `${ERROR_STATES[STATE_IDS.ASSESSMENT_BARRIER]}-${action.meta.arg.barrierId}`
        ];
        state.barriersLoading = false;
        state.barriers = state.barriers.filter(
          (barrier) => barrier.id !== action.meta.arg.barrierId,
        );
        state.errorState = newErrorState;
      },
    )
    .addCase(deleteBarrier.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.barriersLoading = false;
      toast.error(state.error.message);
    })
    // updateBarrier()
    .addCase(updateBarrier.pending, (state: InitialAssessmentState) => {
      state.barriersLoading = true;
      state.error = null;
    })
    .addCase(
      updateBarrier.fulfilled,
      (state: InitialAssessmentState, action: PayloadAction<IOption>) => {
        state.barriersLoading = false;
        state.barriers = state.barriers.map((barrier) => {
          if (barrier.id === action.payload.id) {
            return action.payload;
          }
          return barrier;
        });
      },
    )
    .addCase(updateBarrier.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.barriersLoading = false;
      toast.error(state.error.message);
    })
    // getToolDocuments()
    .addCase(getToolDocuments.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
      state.error = null;
    })
    .addCase(
      getToolDocuments.fulfilled,
      (
        state: InitialAssessmentState,
        action: PayloadAction<IAssessmentVisual[], string, { arg: { toolId: number } }>,
      ) => {
        state.toolsLoading = false;
        state.tools = state.tools.map((tool) => {
          if (tool.id === action.meta.arg.toolId) {
            tool.visuals = action.payload;
            return tool;
          }
          return tool;
        });
      },
    )
    .addCase(getToolDocuments.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // deleteToolDocument()
    .addCase(deleteToolDocument.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
      state.error = null;
    })
    .addCase(
      deleteToolDocument.fulfilled,
      (
        state: InitialAssessmentState,
        action: PayloadAction<
          IAssessmentVisual,
          string,
          { arg: { toolId: number; documentId: number } }
        >,
      ) => {
        const currentTPErrors = state.errorState || {};
        const currentTPPersistedData = state.persistDataState || {};

        const newErrorState = { ...currentTPErrors };
        delete newErrorState[
          `${ERROR_STATES[STATE_IDS.VISUAL]}-${action.meta.arg.toolId}-${action.meta.arg.documentId}`
        ];
        state.errorState = newErrorState;

        delete currentTPPersistedData[
          `${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${action.meta.arg.toolId}-${action.meta.arg.documentId}`
        ];
        state.persistDataState = currentTPPersistedData;

        state.toolsLoading = false;
        state.tools = state.tools.map((tool) => {
          if (tool.id === action.meta.arg.toolId) {
            tool.visuals = tool.visuals?.filter(
              (visual) => visual.id !== action.meta.arg.documentId,
            );
            return tool;
          }
          return tool;
        });
      },
    )
    .addCase(deleteToolDocument.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // createToolDocument()
    .addCase(createToolDocument.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
      state.error = null;
    })
    .addCase(
      createToolDocument.fulfilled,
      (
        state: InitialAssessmentState,
        action: PayloadAction<
          IAssessmentVisual,
          string,
          { arg: { toolId: number; documentId: number } }
        >,
      ) => {
        const currentTPPersistedData = state.persistDataState || {};
        delete currentTPPersistedData[
          `${PERSIST_DATA_STATES[STATE_IDS.VISUAL]}-${action.meta.arg.toolId}-new`
        ];
        state.persistDataState = currentTPPersistedData;
        state.toolsLoading = false;
        state.tools = state.tools.map((tool) => {
          if (tool.id === action.meta.arg.toolId) {
            tool.visuals = tool.visuals ? [...tool.visuals, action.payload] : [action.payload];
            return tool;
          }
          return tool;
        });
        state.error = null;
      },
    )
    .addCase(createToolDocument.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // updateToolDocument()
    .addCase(updateToolDocument.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
      state.error = null;
    })
    .addCase(updateToolDocument.fulfilled, (state: InitialAssessmentState) => {
      state.toolsLoading = false;
    })
    .addCase(updateToolDocument.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    })
    // updateToolDocumentImage()
    .addCase(updateToolDocumentImage.pending, (state: InitialAssessmentState) => {
      state.toolsLoading = true;
      state.error = null;
    })
    .addCase(
      updateToolDocumentImage.fulfilled,
      (
        state: InitialAssessmentState,
        action: PayloadAction<
          IAssessmentVisual,
          string,
          { arg: { toolId: number; documentId: number } }
        >,
      ) => {
        state.toolsLoading = false;
        state.tools = state.tools.map((tool) => {
          if (tool.id === action.meta.arg.toolId) {
            tool.visuals =
              tool.visuals?.map((visual) => {
                if (visual.id === action.payload.id) {
                  return action.payload;
                }
                return visual;
              }) ?? [];
            return tool;
          }
          return tool;
        });
        state.error = null;
      },
    )
    .addCase(updateToolDocumentImage.rejected, (state: InitialAssessmentState, action: any) => {
      state.error = action.error;
      state.toolsLoading = false;
      toast.error(state.error.message);
    });
}

export const assessmentActions = { ...slice.actions, ...extraActions };
export const assessmentReducer = slice.reducer;
